import NiceModal, { useModal } from '@ebay/nice-modal-react'
import Button from 'components/form/button'
import Input from 'components/form/input'
import Select from 'components/form/select'
import Modal from 'components/modal'
import { privateRequest } from 'config/axios.config'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useMutation, useQueryClient } from 'react-query'
import { errorHandler } from 'utils/errorHandler'

/* ---------------- TYPES ---------------- */

type Option = {
  label: string
  value: string
}

interface Payload {
  title: string
  type: string
  status: string
}

interface UpdatePayload extends Payload {
  id: string
}

/* ---------------- OPTIONS ---------------- */

const bankTypeOptions: Option[] = [
  { label: 'Bank', value: 'bank' },
  { label: 'MFS', value: 'mfs' },
]

/* ---------------- MODAL ---------------- */

export default NiceModal.create(({ bank, title }: { bank?: Bank; title: string }) => {
  const modal = useModal()
  const queryClient = useQueryClient()

  /* ---------------- FORM STATE ---------------- */

  const [form, setForm] = useState<{
    title: string
    type: Option
    status: string
  }>({
    title: '',
    type: bankTypeOptions[0],
    status: 'active',
  })

  const [errors, setErrors] = useState<{
    title?: string
    type?: string
    status?: string
  }>({})

  /* ---------------- ADD ---------------- */

  const addbank = useMutation<{ message: string }, Error, Payload>(
    async (payload) => {
      try {
        const res = await privateRequest.post('admin/bank', payload)
        return res.data
      } catch (error) {
        errorHandler(error)
        throw error
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank')
        modal.remove()
      },
    },
  )

  /* ---------------- UPDATE ---------------- */

  const updatebank = useMutation<{ message: string }, Error, UpdatePayload>(
    async (payload) => {
      try {
        const res = await privateRequest.patch('admin/bank', payload)
        return res.data
      } catch (error) {
        errorHandler(error)
        throw error
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank')
        modal.remove()
      },
    },
  )

  /* ---------------- PREFILL (EDIT MODE) ---------------- */

  useEffect(() => {
    if (!bank) return

    setForm({
      title: bank.title ?? '',
      type: bankTypeOptions.find((opt) => opt.value === bank.type) ?? bankTypeOptions[0],
      status: bank.status ?? 'active',
    })
  }, [bank])

  /* ---------------- CHANGE HANDLER ---------------- */

  const changeHandler = ({ target: { name, value } }: _ChangeHandlerEvent) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }))

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }))
  }

  /* ---------------- SUBMIT ---------------- */

  const submitHandler = (e: _FormSubmitEvent) => {
    e.preventDefault()

    if (!form.title || !form.type || !form.status) {
      setErrors({
        title: !form.title ? 'Title is required' : '',
        type: !form.type ? 'Type is required' : '',
        status: !form.status ? 'Status is required' : '',
      })
      return
    }

    const payload: Payload = {
      title: form.title,
      type: form.type.value, // ✅ Option → string
      status: form.status,
    }

    if (bank?._id) {
      toast.promise(updatebank.mutateAsync({ ...payload, id: bank._id }), {
        loading: 'Updating...',
        success: (res) => res.message ?? 'Updated successfully',
        error: (err) => err.message ?? 'Something went wrong',
      })
    } else {
      toast.promise(addbank.mutateAsync(payload), {
        loading: 'Adding...',
        success: (res) => res.message ?? 'Added successfully',
        error: (err) => err.message ?? 'Something went wrong',
      })
    }
  }

  /* ---------------- UI ---------------- */

  return (
    <Modal
      title={title}
      visible={modal.visible}
      onCancel={() => modal.remove()}
      className='max-w-2xl'
    >
      <form onSubmit={submitHandler} className='flex flex-col gap-6'>
        <Input
          name='title'
          label='Title'
          placeholder='Enter Title'
          value={form.title}
          onChange={changeHandler}
          error={!!errors.title}
          helpText={errors.title}
        />

        <Select
          label='Bank Type'
          placeholder='Select Bank type'
          name='type'
          value={form.type}
          onChange={changeHandler}
          options={bankTypeOptions}
          error={!!errors.type}
          helpText={errors.type}
        />

        <Button fullWidth disabled={addbank.isLoading || updatebank.isLoading}>
          Submit
        </Button>
      </form>
    </Modal>
  )
})
